import { setMetaPixelConsent } from "@circle-react/helpers/metaPixel";
import { conversionTrackingQueries } from "@circle-react/lib/queries/conversionTracking";
import type { ConversionTrackingAttributionData } from "@circle-react/lib/queries/conversionTracking";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";

interface UseConversionTrackingAttributionParams {
  hasEnabledProvider: boolean;
  isConsentGranted: boolean;
  metaPixelId?: string;
}

const formatAttributionUrl = (value: string, relative: boolean) => {
  if (!value) return "";

  try {
    const url = new URL(value, globalThis.location?.origin);
    return relative ? `${url.pathname}${url.search}` : url.toString();
  } catch {
    return "";
  }
};

const resolveLandingUrl = (value: string) => {
  const currentLandingUrl = formatAttributionUrl(value, true);
  if (!currentLandingUrl) return "";

  try {
    const currentUrl = new URL(value, globalThis.location?.origin);
    const postLoginRedirect = currentUrl.searchParams.get(
      "post_login_redirect",
    );
    if (!postLoginRedirect) return currentLandingUrl;

    const originalUrl = new URL(postLoginRedirect, currentUrl.origin);
    if (originalUrl.origin !== currentUrl.origin) return currentLandingUrl;

    return (
      formatAttributionUrl(originalUrl.toString(), true) || currentLandingUrl
    );
  } catch {
    return currentLandingUrl;
  }
};

const captureInitialAttributionData =
  (): ConversionTrackingAttributionData => ({
    landing_url: resolveLandingUrl(globalThis.location?.href ?? ""),
    referrer_url:
      typeof document === "undefined"
        ? ""
        : formatAttributionUrl(document.referrer, false),
  });

export const useConversionTrackingAttribution = ({
  hasEnabledProvider,
  isConsentGranted,
  metaPixelId,
}: UseConversionTrackingAttributionParams) => {
  const queryClient = useQueryClient();
  const attributionQuery = useQuery(
    conversionTrackingQueries.attribution({
      hasEnabledProvider,
      initialData: captureInitialAttributionData,
      isConsentGranted,
      queryClient,
    }),
  );
  const { data: attributionData } = attributionQuery;
  const hasFreshAttribution =
    attributionQuery.isFetchedAfterMount && attributionQuery.isSuccess;
  const metaFbc = hasFreshAttribution ? attributionData.meta_fbc : undefined;
  const isReady =
    isConsentGranted &&
    hasEnabledProvider &&
    hasFreshAttribution &&
    !!attributionData.landing_url &&
    typeof attributionData.referrer_url === "string";
  const isCapturePending =
    isConsentGranted &&
    hasEnabledProvider &&
    !attributionQuery.isFetchedAfterMount;

  useEffect(() => {
    if (isCapturePending) return;

    setMetaPixelConsent(
      isConsentGranted ? "granted" : "denied",
      metaPixelId,
      metaFbc,
    );
  }, [isCapturePending, isConsentGranted, metaFbc, metaPixelId]);

  return { attributionData, isCapturePending, isReady, metaPixelId };
};
